import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import LeafletMap from './LeafletMap'
import { useLocationSearch } from '../../hooks/useLocationSearch'
import Button from '../common/Button'
import { inputClass } from '../common/Field'
import { coords as formatCoords } from '../../utils/helpers'

/**
 * The map, given the whole screen.
 *
 * A 160px map inside a form block is fine for confirming a pin and hopeless for
 * placing one — the target is small, the page scrolls under your thumb, and the
 * person doing it may not have steady hands. So the block keeps a still preview
 * and the real work happens here, where the map is as large as the device
 * allows, the pin is draggable, and tapping anywhere moves it.
 *
 * All three ways of answering are in one place: search by name, paste a map
 * link, or put the pin down yourself.
 */
export default function MapSheet({ kind, title, loc, setLoc, onClose }) {
  /**
   * No `kind`, and that is deliberate — not an argument somebody forgot.
   *
   * Passing it would offer this browser's remembered places here too, and this sheet focuses
   * its search box the moment it opens. The list would spring up over a full-screen map that
   * the customer opened *because they wanted the map*, which is the surveying problem the
   * redesign removed. The remembered places belong on the step, where the box is the whole
   * screen's business.
   */
  const { suggestions, handleQueryChange, selectSuggestion } =
    useLocationSearch(loc, setLoc)

  const searchRef = useRef(null)
  const [locating, setLocating] = useState(false)
  const [locateError, setLocateError] = useState(null)

  // What the location was when the sheet opened. Cancel puts it back, so
  // experimenting with the pin is free.
  const openedWith = useRef(loc)

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    searchRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const pick = (lat, lng) =>
    setLoc((prev) => ({
      ...prev,
      lat,
      lng,
      /**
       * A dropped pin has no name, and is now honest about that.
       *
       * It used to be given the coordinates as its label — `14.16942, 121.24191` — which
       * then travelled all the way to the partner's screen and the rider's phone as the
       * delivery address. Six decimals is not an address to anybody. The step now asks the
       * customer what to call it (`PlacePicker`), and they are the only one who knows.
       *
       * `query` is cleared with it: the search text described whatever was typed before
       * the pin was dragged, and leaving it would show a search for one place above a pin
       * somewhere else. That disagreement is the one `useLocationSearch.handleQueryChange`
       * refuses to hold, and it belongs closed at both ends.
       */
      label: '',
      query: '',
    }))

  const cancel = () => {
    setLoc(openedWith.current)
    onClose()
  }

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setLocateError('This browser cannot share a location.')
      return
    }

    setLocating(true)
    setLocateError(null)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        pick(position.coords.latitude, position.coords.longitude)
        setLocating(false)
      },
      () => {
        setLocateError('Location access was refused. Search or drop the pin instead.')
        setLocating(false)
      },
      { timeout: 8000 }
    )
  }

  const placed = loc.lat != null && loc.lng != null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // Layer order in this app: sticky search 20, desktop header 30, bottom
      // bar 40, this sheet above all of them. Leaflet is contained by
      // global.css, so no map can reach up past it.
      className="fixed inset-0 z-[100] flex flex-col bg-shell"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <header className="flex-none flex items-center justify-between gap-3 px-5 py-3 border-b border-hairline">
        <h2 className="text-body font-semibold text-ink truncate">{title}</h2>
        <button
          type="button"
          onClick={cancel}
          aria-label="Close the map"
          className="flex-none w-11 h-11 -mr-2 grid place-items-center rounded-chip text-ink-3
                     transition-colors duration-quick ease-out hover:text-brand"
        >
          <svg
            viewBox="0 0 24 24"
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="flex-none px-5 pt-3 pb-2.5 border-b border-hairline">
        <div className="relative flex items-center">
          <label htmlFor={`${kind}-sheet-search`} className="sr-only">
            Search for a place
          </label>

          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="absolute left-3 w-4 h-4 text-ink-3 pointer-events-none"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>

          <input
            id={`${kind}-sheet-search`}
            ref={searchRef}
            type="text"
            autoComplete="off"
            value={loc.query}
            onChange={(event) => handleQueryChange(event.target.value)}
            placeholder="Search a place, street or barangay"
            className={`${inputClass(false)} pl-9`}
          />
        </div>

        {/* The results float, so appearing does not shove the map down. */}
        {suggestions !== null && (
          <div className="relative">
            <ul className="absolute inset-x-0 top-1 z-10 max-h-[220px] overflow-auto rounded-chip border border-edge bg-surface shadow-sheet">
              {suggestions === 'error' && (
                <li className="px-3.5 py-2.5 text-body-s text-ink-3">
                  Search is unavailable. Paste a map link, or drop the pin yourself.
                </li>
              )}

              {suggestions !== 'error' && suggestions.length === 0 && (
                <li className="px-3.5 py-2.5 text-body-s text-ink-3">No matches found</li>
              )}

              {suggestions !== 'error' &&
                suggestions.map((suggestion, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      onClick={() => selectSuggestion(suggestion)}
                      className="w-full min-h-[48px] px-3.5 py-2.5 text-left text-body-s text-ink
                                 border-b border-hairline last:border-b-0
                                 transition-colors duration-quick ease-out hover:bg-blush"
                    >
                      {suggestion.label}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-2">
          {/* 44px, not 36px — everything reachable in here has to stay easy to
              hit for the person this screen exists for. */}
          <Button
            variant="ghost"
            size="compact"
            onClick={useMyLocation}
            disabled={locating}
            className="flex-1"
          >
            {locating ? 'Locating…' : 'Use my location'}
          </Button>
        </div>


        {locateError && (
          <p role="alert" className="mt-2 text-body-s text-alert">
            {locateError}
          </p>
        )}
      </div>

      {/* min-h-0 is what lets this actually shrink to the space left over. */}
      <div className="flex-1 min-h-0">
        <LeafletMap
          lat={loc.lat}
          lng={loc.lng}
          onPick={pick}
          variant={kind}
          interactive
          className="w-full h-full"
        />
      </div>

      <footer className="flex-none px-5 pt-3 pb-3 border-t border-hairline bg-shell">
        <p className="text-label font-semibold uppercase text-ink-3">
          {placed ? 'Pin is here' : 'No pin yet'}
        </p>
        <p className="mt-1 text-body-s text-ink-2 tabular-nums break-words">
          {placed ? formatCoords(loc.lat, loc.lng) : 'Pindutin po ang mapa para maglagay ng pin, o maghanap sa itaas.'}
        </p>

        <div className="flex gap-3 mt-3">
          <Button variant="ghost" size="compact" onClick={cancel} className="flex-none">
            Cancel
          </Button>
          <Button size="compact" onClick={onClose} disabled={!placed} className="flex-1">
            Use this location
          </Button>
        </div>
      </footer>
    </div>,
    document.body
  )
}
